import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  DollarSign, 
  Target, 
  Phone,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  AlertCircle
} from 'lucide-react';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useVisibility } from '@/hooks/useVisibility';

interface CustomerMetrics {
  totalLifetimeValue: number;
  totalJobs: number;
  completedJobs: number;
  cancelledJobs: number;
  activeJobs: number;
  totalLeads: number;
  convertedLeads: number;
  conversionRate: number;
  averageJobValue: number;
  outstandingBalance: number;
  totalPaid: number;
  totalCommunications: number;
  lastContactDate?: string;
  paymentReliability: 'excellent' | 'good' | 'fair' | 'poor';
}

interface CustomerMetricsCardProps {
  metrics: CustomerMetrics;
}

const reliabilityColors = {
  excellent: 'bg-success',
  good: 'bg-info',
  fair: 'bg-warning',
  poor: 'bg-destructive',
};

const reliabilityLabels = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  poor: 'Needs Attention',
};

export function CustomerMetricsCard({ metrics }: CustomerMetricsCardProps) {
  const { formatDate } = useTimezoneUtils();
  const { canSeeCompanyFinancials } = useVisibility();

  return (
    <div className={`grid gap-4 md:grid-cols-2 ${canSeeCompanyFinancials ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
      {/* Financial Metrics */}
      {canSeeCompanyFinancials && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-success/10 flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-success" />
            </div>
            Financial Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground">Lifetime Value</p>
            <p className="text-2xl font-bold text-success">
              ${metrics.totalLifetimeValue.toFixed(2)}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-muted-foreground">Avg Job Value</p>
              <p className="text-sm font-semibold">
                ${metrics.averageJobValue.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Outstanding</p>
              <p className={`text-sm font-semibold ${metrics.outstandingBalance > 0 ? 'text-destructive' : 'text-success'}`}>
                ${metrics.outstandingBalance.toFixed(2)}
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Payment Reliability</p>
            <Badge className={`${reliabilityColors[metrics.paymentReliability]} mt-1`}>
              {reliabilityLabels[metrics.paymentReliability]}
            </Badge>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Job Metrics */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-info/10 flex items-center justify-center">
              <BarChart3 className="h-4 w-4 text-info" />
            </div>
            Job Statistics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground">Total Jobs</p>
            <p className="text-2xl font-bold">{metrics.totalJobs}</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="flex items-center gap-1 mb-1">
                <CheckCircle className="h-3 w-3 text-success" />
                <p className="text-xs text-muted-foreground">Done</p>
              </div>
              <p className="text-sm font-semibold">{metrics.completedJobs}</p>
            </div>
            <div>
              <div className="flex items-center gap-1 mb-1">
                <Clock className="h-3 w-3 text-warning" />
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
              <p className="text-sm font-semibold">{metrics.activeJobs}</p>
            </div>
            <div>
              <div className="flex items-center gap-1 mb-1">
                <XCircle className="h-3 w-3 text-destructive" />
                <p className="text-xs text-muted-foreground">Lost</p>
              </div>
              <p className="text-sm font-semibold">{metrics.cancelledJobs}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lead Metrics */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Target className="h-4 w-4 text-primary" />
            </div>
            Lead Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground">Conversion Rate</p>
            <p className="text-2xl font-bold text-primary">
              {metrics.conversionRate.toFixed(0)}%
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-muted-foreground">Total Leads</p>
              <p className="text-sm font-semibold">{metrics.totalLeads}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Converted</p>
              <p className="text-sm font-semibold text-success">
                {metrics.convertedLeads}
              </p>
            </div>
          </div>
          {metrics.conversionRate < 20 && metrics.totalLeads > 5 && (
            <div className="flex items-center gap-2 text-xs text-warning">
              <AlertCircle className="h-3 w-3" />
              <span>Low conversion rate</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Engagement Metrics */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-warning/10 flex items-center justify-center">
              <Phone className="h-4 w-4 text-warning" />
            </div>
            Customer Engagement
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground">Total Interactions</p>
            <p className="text-2xl font-bold">{metrics.totalCommunications}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Last Contact</p>
            <p className="text-sm font-semibold">
              {metrics.lastContactDate 
                ? formatDate(new Date(metrics.lastContactDate), 'MMM dd, yyyy')
                : 'Never'}
            </p>
          </div>
          {metrics.lastContactDate && (
            <div>
              <p className="text-xs text-muted-foreground">Response Time</p>
              <Badge variant="outline" className="mt-1">
                {(() => {
                  const daysSince = Math.floor(
                    (Date.now() - new Date(metrics.lastContactDate).getTime()) / (1000 * 60 * 60 * 24)
                  );
                  if (daysSince === 0) return 'Today';
                  if (daysSince === 1) return 'Yesterday';
                  if (daysSince < 7) return `${daysSince} days ago`;
                  if (daysSince < 30) return `${Math.floor(daysSince / 7)} weeks ago`;
                  return `${Math.floor(daysSince / 30)} months ago`;
                })()}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}